import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

export default function LegendDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 })

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8, // 8px gap below button
        right: window.innerWidth - rect.right
      })
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="fixed w-72 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[9999]"
      style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
    >
      <div className="p-4" role="menu" aria-orientation="vertical">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Market Change Highlighting</h3>
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 bg-blue-100 border border-blue-200 rounded flex-shrink-0"></div>
            <span className="text-sm text-gray-700">Price Changes</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 bg-purple-100 border border-purple-200 rounded flex-shrink-0"></div>
            <span className="text-sm text-gray-700">New Listings</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 bg-yellow-100 border border-yellow-200 rounded flex-shrink-0"></div>
            <span className="text-sm text-gray-700">Under Contract</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 bg-green-100 border border-green-200 rounded flex-shrink-0"></div>
            <span className="text-sm text-gray-700">Off-Market</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
          Click highlighted aircraft to view details and acknowledge changes
        </p>
      </div>
    </div>
  ) : null

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
        Legend
        <svg className={`ml-2 w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {createPortal(dropdownContent, document.body)}
    </div>
  )
}

